export const QCFlowStates = {
  AWAITING_ORDER: 'awaiting_order',
  ORDER_LOADING: 'order_loading',
  SCANNING_ITEMS: 'scanning_items',
  SERIAL_CAPTURE: 'serial_capture',
  AWAITING_RESCAN: 'awaiting_rescan',
  PRINTING: 'printing',
  COMPLETED: 'completed',
} as const;

export const QCFlowEvents = {
  SCAN_ORDER_BARCODE: 'scan_order_barcode',
  ORDER_LOADED: 'order_loaded',
  ORDER_LOADED_COMPLETED: 'order_loaded_completed',  // 訂單載入且所有品項已完成
  LOAD_FAILED: 'load_failed',
  ITEM_REQUIRES_SERIAL: 'item_requires_serial',
  SERIAL_CAPTURED: 'serial_captured',
  ALL_ITEMS_COMPLETED: 'all_items_completed',
  ITEM_ADJUSTMENT: 'item_adjustment',
  RESCAN_ORDER_FOR_SHIPPING: 'rescan_order_for_shipping',
  PRINT_SUCCESS: 'print_success',
  PRINT_FAILED_OR_CANCELED: 'print_failed_or_canceled',
  ABORT_ORDER: 'abort_order',
  RESET_FLOW: 'reset_flow',
} as const;

export type QCFlowState = (typeof QCFlowStates)[keyof typeof QCFlowStates];
export type QCFlowEvent = (typeof QCFlowEvents)[keyof typeof QCFlowEvents];

export const qcFlowTransitions: Record<QCFlowState, Partial<Record<QCFlowEvent, QCFlowState>>> = {
  [QCFlowStates.AWAITING_ORDER]: {
    [QCFlowEvents.SCAN_ORDER_BARCODE]: QCFlowStates.ORDER_LOADING,
    [QCFlowEvents.RESET_FLOW]: QCFlowStates.AWAITING_ORDER,
  },
  [QCFlowStates.ORDER_LOADING]: {
    [QCFlowEvents.ORDER_LOADED]: QCFlowStates.SCANNING_ITEMS,
    [QCFlowEvents.ORDER_LOADED_COMPLETED]: QCFlowStates.AWAITING_RESCAN,  // 訂單已完成，直接進入等待複掃
    [QCFlowEvents.LOAD_FAILED]: QCFlowStates.AWAITING_ORDER,
    [QCFlowEvents.ABORT_ORDER]: QCFlowStates.AWAITING_ORDER,
  },
  [QCFlowStates.SCANNING_ITEMS]: {
    [QCFlowEvents.ITEM_REQUIRES_SERIAL]: QCFlowStates.SERIAL_CAPTURE,
    [QCFlowEvents.ALL_ITEMS_COMPLETED]: QCFlowStates.AWAITING_RESCAN,
    [QCFlowEvents.ABORT_ORDER]: QCFlowStates.AWAITING_ORDER,
  },
  [QCFlowStates.SERIAL_CAPTURE]: {
    [QCFlowEvents.SERIAL_CAPTURED]: QCFlowStates.SCANNING_ITEMS,
    [QCFlowEvents.ABORT_ORDER]: QCFlowStates.AWAITING_ORDER,
  },
  [QCFlowStates.AWAITING_RESCAN]: {
    [QCFlowEvents.RESCAN_ORDER_FOR_SHIPPING]: QCFlowStates.PRINTING,
    [QCFlowEvents.ITEM_ADJUSTMENT]: QCFlowStates.SCANNING_ITEMS,
    [QCFlowEvents.ABORT_ORDER]: QCFlowStates.AWAITING_ORDER,
  },
  [QCFlowStates.PRINTING]: {
    [QCFlowEvents.PRINT_SUCCESS]: QCFlowStates.COMPLETED,
    [QCFlowEvents.PRINT_FAILED_OR_CANCELED]: QCFlowStates.AWAITING_RESCAN,
    [QCFlowEvents.ABORT_ORDER]: QCFlowStates.AWAITING_ORDER,
  },
  [QCFlowStates.COMPLETED]: {
    [QCFlowEvents.RESET_FLOW]: QCFlowStates.AWAITING_ORDER,
    [QCFlowEvents.SCAN_ORDER_BARCODE]: QCFlowStates.ORDER_LOADING,
  },
};

export const QCFlowStateLabels: Record<QCFlowState, string> = {
  [QCFlowStates.AWAITING_ORDER]: '等待訂單掃描',
  [QCFlowStates.ORDER_LOADING]: '訂單載入中',
  [QCFlowStates.SCANNING_ITEMS]: '逐項掃描',
  [QCFlowStates.SERIAL_CAPTURE]: '序號輸入',
  [QCFlowStates.AWAITING_RESCAN]: '等待訂單複掃',
  [QCFlowStates.PRINTING]: '列印托運單',
  [QCFlowStates.COMPLETED]: '流程完成',
};

export function getNextQcFlowState(currentState: QCFlowState, event: QCFlowEvent): QCFlowState | null {
  const stateTransitions = qcFlowTransitions[currentState];
  if (!stateTransitions) {
    return null;
  }
  return stateTransitions[event] || null;
}

export function isQcFlowEventAllowed(currentState: QCFlowState, event: QCFlowEvent): boolean {
  return Boolean(getNextQcFlowState(currentState, event));
}
